/** Badge ids/text values (normalized) that represent a "Sale" badge and must be hidden for ESW/international locales. */
const SALE_BADGE_IDS = new Set([
    'sale',
    'finalsale',
    'final_sale',
    'isuksale',
    'isussale',
    'iscasale',
    'grgbadgeuksale',
    'grgbadgeussale',
    'grgbadgecasale'
])

/**
 * Lowercases a value and strips non-alphanumeric characters so ids/text can be compared
 * regardless of casing or separator style (e.g. `grg_badge_uk_sale` vs `grgBadgeUkSale`).
 * @param {*} value
 * @returns {string}
 */
const normalizeBadgeId = (value) =>
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')

/**
 * Determines whether a badge represents a "Sale" badge, checking either its id or its
 * display text against {@link SALE_BADGE_IDS}.
 * @param {string|{id?: string, text?: string}} badge - Badge id string, or a badge-like object.
 * @returns {boolean} True if the badge is a sale badge.
 */
export const isSaleBadge = (badge) => {
    if (typeof badge === 'string') {
        return SALE_BADGE_IDS.has(normalizeBadgeId(badge))
    }
    return (
        SALE_BADGE_IDS.has(normalizeBadgeId(badge?.id)) ||
        SALE_BADGE_IDS.has(normalizeBadgeId(badge?.text))
    )
}

/**
 * Determines whether a sale badge should be hidden for the given locale. Sale badges are
 * only hidden for ESW/international locales; domestic locales are unaffected.
 * @param {{isSupportedByESW?: boolean}} locale - The active locale from `useMultiSite()`.
 * @param {string|{id?: string, text?: string}} badge - Badge id string, or a badge-like object.
 * @returns {boolean} True if the badge should be hidden.
 */
export const shouldHideSaleBadgeForLocale = (locale, badge) =>
    Boolean(locale?.isSupportedByESW && isSaleBadge(badge))

/** Category ids for the "Sale" and "Gift Cards" merchandising categories, hidden for ESW/international locales. */
const HIDDEN_ESW_MERCH_CATEGORY_IDS = new Set(['grg_sale', 'grg_main_giftcards'])

/**
 * Determines whether a merchandising category (e.g. "Sale", "Gift Cards") should be hidden
 * from navigation, search suggestions, etc. for the given locale. Categories are matched by
 * id only, against {@link HIDDEN_ESW_MERCH_CATEGORY_IDS}. Only applies to ESW/international
 * locales; domestic locales are unaffected.
 * @param {{isSupportedByESW?: boolean}} locale - The active locale from `useMultiSite()`.
 * @param {{id?: string}} category - A category-like object.
 * @returns {boolean} True if the category should be hidden.
 */
export const shouldHideMerchCategoryForLocale = (locale, category) => {
    if (!locale?.isSupportedByESW || !category) return false

    const categoryId = String(category.id || '').toLowerCase()

    return HIDDEN_ESW_MERCH_CATEGORY_IDS.has(categoryId)
}
